/**
 * NEXORAX EMPIRE OS - GLOBAL CLIENT RECORD & INVOICE PROTOCOL
 * Mandate: Universal targeting, Mandatory Data Capture, Unique Invoicing
 */

export interface ClientDealInput {
  companyName: string;
  address: string;
  contactName: string;
  email: string;
  amount: number;
}

export interface ClientDealRecord {
  invoiceRef: string;
  companyName: string;
  address: string;
  contactName: string;
  email: string;
  amount: number;
  status: "PENDING_VERIFIED_PAYMENT" | "PAID" | "SETTLED";
  timestamp: string;
}

export class NexoraXGlobalRegistry {
  private ledgerRefKey = "nexorax_global_registry_ledger_v2";
  private counterRefKey = "nexorax_global_registry_counter_v2";

  constructor() {
    if (typeof window !== "undefined") {
      console.log("Global Registry Protocol: ACTIVE. Global targeting scanning initialized.");
      if (!localStorage.getItem(this.counterRefKey)) {
        localStorage.setItem(this.counterRefKey, "1000");
      }
    }
  }

  get ledger(): ClientDealRecord[] {
    if (typeof window === "undefined") return [];
    try {
      const stored = localStorage.getItem(this.ledgerRefKey);
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      console.error("Error reading global registry ledger:", e);
      return [];
    }
  }

  set ledger(value: ClientDealRecord[]) {
    if (typeof window !== "undefined") {
      try {
        localStorage.setItem(this.ledgerRefKey, JSON.stringify(value));
      } catch (e) {
        console.error("Error writing global registry ledger:", e);
      }
    }
  }

  private getNextInvoiceCounter(): number {
    if (typeof window === "undefined") return 1001;
    const current = parseInt(localStorage.getItem(this.counterRefKey) || "1000", 10);
    const nextVal = current + 1;
    localStorage.setItem(this.counterRefKey, nextVal.toString());
    return nextVal;
  }

  // Mandatory Protocol: Capture Client Details before any transaction
  registerClientDeal(clientData: ClientDealInput): ClientDealRecord {
    const { companyName, address, contactName, email, amount } = clientData;
    
    // Validate mandatory data
    if (!companyName || !address || !contactName) {
      throw new Error("MANDATORY_DATA_MISSING: Cannot proceed without full corporate record.");
    }

    const currentLedger = this.ledger;
    const nextCounter = this.getNextInvoiceCounter();
    const invoiceRef = `NX-${nextCounter}`;

    const dealRecord: ClientDealRecord = {
      invoiceRef,
      companyName,
      address,
      contactName,
      email,
      amount,
      status: "PENDING_VERIFIED_PAYMENT",
      timestamp: new Date().toISOString()
    };

    currentLedger.unshift(dealRecord);
    this.ledger = currentLedger;

    console.log(`[LEDGER] Client registered. Invoice Reference: ${invoiceRef}`);
    return dealRecord;
  }

  // Mark invoice as paid
  markAsPaid(invoiceRef: string): boolean {
    const currentLedger = this.ledger;
    const item = currentLedger.find(c => c.invoiceRef === invoiceRef);
    if (item) {
      item.status = "SETTLED";
      this.ledger = currentLedger;
      return true;
    }
    return false;
  }
}

export const GlobalRegistry = new NexoraXGlobalRegistry();
